import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import {
  useNotify,
  useRedirect,
  FormDataConsumer,
  SaveButton,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";

import {
  ApproveIcon,
  RejectIcon,
} from "../../../components/common/iconography";
import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  CarrierType,
  FilePaths,
  InboundState,
  InboundStatus,
  InventoryStatus,
  NoteType,
  WorkflowType,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import { isTrack } from "../../../utils/locationUtils";
import {
  getCarrierEquipmentTypeNameById,
  isBulkShipment,
} from "../../../utils/loadTypeUtils";
import {
  areAllSealsValid,
  deepRedirect,
  getLogState,
  historyAutoGen,
  buildTimePulledNote,
  normalizeValue,
} from "../../../utils/utils";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import {
  autogenLotNumber,
  calcNetWeight,
  emailPOD,
  formValidate,
  isExternalAdvice,
  resetShipmentExceptions,
  updateStripeUsage,
  validateInboundUnicity,
  validateRailcarRelease,
  validateReleaseCommitment,
} from "./InboundUtils";
import {
  areTimeslotsEqual,
  getYardCalendarDaysInclusive,
  validateAppointment,
} from "../../../utils/dateUtils";
import { isCollectedCleared } from "../../../utils/shipmentPlacement";
import { API, graphqlOperation } from "aws-amplify";
import { updateProduct } from "../../../graphql/mutations";
import {
  fetchActiveGateCheckIn,
  GATE_TERMINAL_STATUSES,
  GateCheckInConflictError,
  resolveStagedGateEdit,
  stagedGateEditRefusal,
} from "../gateCheckIns/gateCheckInUtils";
import { runGateSideEffects } from "../gateCheckIns/onShipmentSaved";
import { AdviceRejectButton } from "./advice/AdviceRejectButton";
import { emailInboundAdviceApprovedHTML } from "./emailInboundAdviceHTML";

const useStyles = makeStyles((theme) => ({
  rejectButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      // Reset on mouse devices
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  saveButton: {
    marginRight: theme.spacing(2),
  },
}));

export const DialogSaveButton = ({
  record,
  validateTallySheetSaved,
  validateTallyQty,
  basePath,
  backPath,
  loading,
  isSaving,
  setIsSaving,
  isDeleting,
  operatingAccount,
  selectedTimeslot,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [confirmContent, setConfirmContent] = useState([]);
  const [approve, setApprove] = useState(true);
  // Set in transform (where the "POD sent to:" history note is added) to
  // { record, effectiveAccount }; onSuccess sends the email only after
  // persistence. Reset at the top of every transform so a pending email from
  // a FAILED save cannot leak into a later, non-release save.
  const podEmailPendingRef = useRef(false);
  // The active check-in stranded by an arrival rollback, found in transform;
  // onSuccess cancels it only after the shipment write commits. Cancelling
  // first meant a failed save left a CANCELLED check-in still holding the
  // shipment's gateCheckInId lock, blocking the next driver.
  const pendingGateCancelRef = useRef(false);
  // { target, gateEdit } for the dispatch tab's staged gate edit, resolved
  // (and conflict-checked) in transform; onSuccess commits it only after the
  // shipment write lands. Committing in transform meant a save that failed
  // later (file upload, lambda write) had already advanced the gate row for
  // a shipment that was never saved.
  const pendingGateEditRef = useRef(false);
  // The shipment as transform is about to write it, for the manual gate
  // check-in onSuccess may mint from it. Stashed here for the same reason the
  // POD body is: the `data` onSuccess receives is the dataProvider's sanitized
  // return, and reading an arrival stamp that had been stripped out of it
  // would silently skip the row for every truck the office waves in.
  const pendingManualCheckInRef = useRef(false);
  // The driver, plate, dock and status the dispatch tab staged for a check-in
  // that has no row yet — the office checking a truck in from the form. It
  // opens the row the mint above creates, so unlike a staged edit it is never
  // resolved against an existing one.
  const pendingManualOpeningRef = useRef(false);
  const getEffectiveAccount = (data) =>
    operatingAccount || data?.account || record.account;

  const getApproveLabel = () => {
    // if (formData.state === InboundState.APPROVE_INVENTORY_REVIEW) return "Compplete";
    return "Approve";
  };

  const isStatusRollback = (data) => {
    const newStatus = data.status;
    const newState = data.state;
    const oldState = record.state;

    return (
      (newStatus === InboundStatus.IN_TRANSIT ||
        newStatus === InboundStatus.SIT_PENDING ||
        newStatus === InboundStatus.ARRIVED ||
        newStatus === InboundStatus.PLACED ||
        newStatus === InboundStatus.SIT_PLACED) &&
      (newState === InboundState.NEW ||
        newState === InboundState.PROCESS_SHIPMENT) &&
      oldState !== InboundState.NEW &&
      oldState !== InboundState.PROCESS_SHIPMENT
    );
  };

  const validateRollback = (formData) => {
    const data = { ...formData };
    debugger;

    setNextState(data);
    if (isStatusRollback(data)) {
      const committed = data.inventories.items.some(
        (inv) => inv.shipmentLineItems?.items.length > 0,
      );
      // committed inventories on rollback. Show warning.
      if (committed) return true;
    }
    return false;
  };

  const handleValidateSave = async (formData) => {
    const effectiveAccount = getEffectiveAccount(formData);
    let tempMsg = [];
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error

    const isValid = formValidate(form, notify);
    if (isValid) {
      // A supervisor may only call the truck at the front of the gate queue,
      // and a visit only ends as DEPARTED from the dock, once the load is
      // worked. This is the last
      // point either refusal can stop the save cleanly: from the submit onward
      // a refusal only rejects react-final-form's submit promise, which
      // nothing catches, so it reached the manager as an unhandled error where
      // a warning belongs. The staged edit is left alone — the manager fixes
      // the status in the select they staged it in, or the truck's turn comes
      // and the same save then goes through.
      const refusal = await stagedGateEditRefusal(formData.gateCheckInEdit, {
        ...record,
        ...formData,
      });
      if (refusal) {
        notify(`${refusal} — nothing was saved.`, "warning");
        return;
      }

      const duplicateNumbers = await validateInboundUnicity(formData);
      if (duplicateNumbers.length > 0) {
        tempMsg.push(
          "Possible duplicate with this " +
            duplicateNumbers.join(" & ") +
            " Number already exists.",
        );
      }
      if (
        effectiveAccount?.trackInboundSeals &&
        !formData?.sealNumbers?.length
      ) {
        tempMsg.push(
          "No seal numbers were added. Proceed saving without tracking seals?",
        );
      }

      if (formData.inventories && formData.inventories.items) {
        if (validateTallySheetSaved(formData.inventories.items)) {
          tempMsg.push(
            "There are un-saved Tally Sheet items. Un-saved changes will be lost.",
          );
        }
        if (validateTallyQty(formData)) {
          tempMsg.push("Quantity Advised and Received do not match!");
        }
        // Committed inventory refuses the release outright — the heel warning
        // below offers to write inventory off, and inventory an outbound is
        // waiting on is not the manager's to write off from here.
        const releaseCommitment = validateReleaseCommitment(formData);
        const remainingInventory = validateRailcarRelease(formData);
        if (releaseCommitment) {
          tempMsg.push(releaseCommitment);
          setOnConfirm(null); // Prevent saving if there's an error
        } else if (remainingInventory) {
          tempMsg.push(
            remainingInventory +
              " of remaining Inventory on hand will be removed.",
          );
        } else if (validateRollback(formData)) {
          tempMsg.push(
            "Inventory committed to a pending Outbound shipment. " +
              "Unable to rollback until inventory is removed from Outbound.",
          );
          setOnConfirm(null); // Prevent saving if there's an error
        }
      }
      // Only re-validate when the appointment actually changed (mirrors the
      // outbound DialogSaveButton) — an unchanged existing booking must not
      // block unrelated edits if its slot has since gone over capacity.
      if (
        selectedTimeslot &&
        !areTimeslotsEqual(record.timeslot, selectedTimeslot)
      ) {
        try {
          const isBooked = await validateAppointment(
            selectedTimeslot,
            record.timeslotId,
          );
          if (isBooked) {
            tempMsg.push(
              `${selectedTimeslot.time} is no longer available. Please select a new time.`,
            );
            setOnConfirm(null);
          }
        } catch (error) {
          setOnConfirm(null);
          logError(
            "inboundShipments:DialogSaveButton:handleValidateSave:timeslot",
            error,
            notify,
          );
          return;
        }
      }
    }

    if (tempMsg.length === 1) {
      setConfirmContent(tempMsg[0]);
      setOpen(true);
    } else if (tempMsg.length > 1) {
      setConfirmContent(
        <ul>
          {tempMsg.map((msg) => (
            <li>{msg}</li>
          ))}
        </ul>,
      );
      setOpen(true);
    } else handleSave();
  };

  const handleValidateApprove = (formData) => {
    let tempMsg = null;
    let allApproved = false;
    let allApprovedAdvice = false;
    let allSealsValidated = false;
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
    debugger;

    const isValid = formValidate(form, notify);

    if (isValid) {
      // check if only the shipment needs approval but none of inventories
      allApproved = formData.verificationRequired === "true";
      allApprovedAdvice = formData.verificationRequired === "true";
      if (formData.inventories && formData.inventories.items) {
        allApproved = !formData.inventories.items.some(
          (row) =>
            row?.verificationRequired === "true" &&
            !row?.status?.startsWith(InboundStatus.ADVICE_ALL),
        );
        allApprovedAdvice = !formData.inventories.items.some(
          (row) =>
            row?.verificationRequired === "true" &&
            row?.status?.startsWith(InboundStatus.ADVICE_ALL),
        );
      }
      if (
        formData?.inValidSealNumber?.length > 0 &&
        formData?.inValidSeals?.items?.length > 0
      ) {
        allSealsValidated = areAllSealsValid(
          formData?.inValidSealNumber,
          formData?.inValidSeals?.items,
        );
      }

      if (!allApprovedAdvice) {
        tempMsg = (
          <>
            The AI agent was not fully confident that the inventories marked for
            review were matched to the correct products. <br />
            Review and approve these inventories to accept the advice.
          </>
        );
        setApprove(true);
        setOnConfirm(() => handleDialogConfirmApproveAdvice); // disable confirm
      } else if (!allApproved) {
        tempMsg =
          "Errors Found : Approve all inventories first! Shipment will not be Approved.";
        setApprove(false);
        setOnConfirm(null); // disable confirm
      } else if (formData.brokenSeal) {
        if (allSealsValidated) {
          tempMsg = (
            <>
              All seals are valid. <br />
              To clear the seal exception click CANCEL and open the Exception
              Manager. <br />
              Proceed with approval?
            </>
          );
          setApprove(true);
        } else {
          // CREW should be performing RailCar Validation on each inbound,
          // even if railcar is already placed through Track Management.
          // This is to avoid CREW scanning seals on the wronng railcar.
          tempMsg = (
            <>
              Errors found: Not all seals have been validated! <br />
              Please reconcile the seals in the Seal Manager.
            </>
          );
          setApprove(false);
          setOnConfirm(null); // disable confirm
        }
      } else {
        if (validateTallyQty(formData)) {
          // Check quantities Advised & Received
          tempMsg = "Warning: Quantity Advised and Received do not match!";
          setApprove(false);
        } else {
          // check if resolution documented in notes
          const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;

          if (
            notesMaxIndex < 0 ||
            (notesMaxIndex >= 0 &&
              (!formData.notes[notesMaxIndex] ||
                formData.notes[notesMaxIndex].date))
          ) {
            tempMsg =
              "Don't forget to document issue resolution in the Notes! ";
          }
        }
      }
    }

    if (tempMsg && tempMsg !== "") {
      debugger;
      setConfirmContent(tempMsg);
      setOpen(true);
    } else handleSave();
  };

  const handleValidateReject = (formData) => {
    let tempMsg = null;
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
    debugger;

    const isValid = formValidate(form, notify);
    if (isValid) {
      // check if rejection documented in notes
      const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;

      if (
        notesMaxIndex < 0 ||
        (notesMaxIndex >= 0 &&
          (!formData.notes[notesMaxIndex] ||
            formData.notes[notesMaxIndex].date))
      ) {
        tempMsg =
          "Don't forget to document rejection cause in the Notes! Proceed with Rejection?";
      }
    }

    if (tempMsg && tempMsg !== "") {
      debugger;
      setConfirmContent(tempMsg);
      setOpen(true);
    } else handleSave();
  };

  const handleDialogConfirm = () => {
    const formData = form.getState().values;
    debugger;

    // remove unsaved / empty inventories.
    const tallySheet = formData.inventories.items.filter(
      (itm) => typeof itm !== `undefined` && itm.status !== InventoryStatus.NEW,
    );
    form.change("inventories.items", tallySheet);

    handleSave();
  };
  const handleDialogConfirmApproveAdvice = async () => {
    const formData = form.getState().values;
    debugger;
    formData.status = formData.sit
      ? InboundStatus.SIT_PENDING
      : InboundStatus.IN_TRANSIT;
    form.change("status", formData.status);
    // Merge existing product altNames with braRawProductName and productName (Set); same logic as AppSync product update
    const updateAltNames = (data) => {
      const altNamesSet = new Set();
      data.product?.altNames?.forEach((altName) => {
        if (altName != null && String(altName).trim() !== "") {
          altNamesSet.add(altName);
        }
      });
      if (
        data?.braRawProductName != null &&
        String(data.braRawProductName).trim() !== ""
      ) {
        altNamesSet.add(data.braRawProductName);
      }
      if (data?.productName != null && String(data.productName).trim() !== "") {
        altNamesSet.add(data.productName);
      }
      return Array.from(altNamesSet);
    };
    formData.inventories.items.forEach((element) => {
      if (element.product) {
        element.product.altNames = updateAltNames(element);
      }
    });
    await Promise.all(
      formData.inventories.items.map(async (element) => {
        if (!(element?.braRawProductName || element?.productName)) {
          return;
        }
        const productId = element.product?.id;
        if (!productId) {
          return;
        }
        try {
          const productAltNames = updateAltNames(element);
          const result = await API.graphql(
            graphqlOperation(updateProduct, {
              input: {
                id: productId,
                altNames: productAltNames,
              },
            }),
          );
        } catch (error) {
          notify("Error updating product altNames", error, notify);
        }
      }),
    );

    formData.inventories.items.forEach((element) => {
      element.status = InventoryStatus.ARRIVED;
      element.verificationRequired = "resolved";
    });
    formData.history = [
      ...formData.history,
      {
        text: `Advice shipment approved | by ${window.appUser?.fullName}`,
        type: NoteType.SHIPMENT,
        date: new Date(),
        by: window.appUser?.fullName,
      },
    ];
    form.change("history", formData.history);
    form.change("inventories.items", formData.inventories.items);

    handleSave();
  };
  const handleDialogClose = () => setOpen(false);

  const [onConfirm, setOnConfirm] = useState(() => handleDialogConfirm);

  const handleSave = () => {
    setOpen(false);
    if (approve) props.handleSubmitWithRedirect();
  };

  const approveTransform = async (data) => {
    setIsSaving(true);
    debugger;

    // Manager does not ACCEPT shipments, only APPROVE and
    // send back to CREW to contineue inspections.
    // Only CREW can ACCEPT shipment after completing all inspections and
    // after MANAGER approved all exceptions.

    if (
      data.status === InboundStatus.ADVICE_REVIEW ||
      data.status === InboundStatus.ADVICE_FAILED
    ) {
      data.status = data.sit
        ? InboundStatus.SIT_PENDING
        : InboundStatus.IN_TRANSIT;
    }
    if (data.state === InboundState.APPROVE_SHIPMENT) {
      data.state = InboundState.PROCESS_SHIPMENT;
      data.verificationRequired = "resolved";
      data.startDateTime = new Date();

      let note = {
        text: `Shipment Approved`,
        type: NoteType.SHIPMENT,
        date: new Date(),
        by: window.appUser?.fullName,
      };
      if (data.history) data.history = [...data.history, note];
      else data.history = [note];

      if (data.inventories?.items) {
        data.inventories.items.forEach((element) => {
          element.status = InventoryStatus.ARRIVED;
          if (element.history) element.history = [...element.history, note];
          else element.history = [note];
        });
      }
    } else if (data.state === InboundState.APPROVE_INVENTORY) {
      data.state = InboundState.PROCESS_INVENTORY;
      data.verificationRequired = "resolved";

      let note = {
        text: `Inventory Approved`,
        type: NoteType.INVENTORY,
        date: new Date(),
        by: window.appUser?.fullName,
      };
      if (data.history) data.history = [...data.history, note];
      else data.history = [note];

      if (data.inventories?.items) {
        data.inventories.items.forEach((element) => {
          element.verificationRequired =
            element.verificationRequired === "true"
              ? "resolved"
              : element.verificationRequired;
          if (element.history) element.history = [...element.history, note];
          else element.history = [note];
        });
      }
    } else if (data.state === InboundState.APPROVE_INVENTORY_REVIEW) {
      data.state = InboundState.PROCESS_INVENTORY_REVIEW;
      data.verificationRequired = "resolved";

      let note = {
        text: `Inventory Review Completed`,
        type: NoteType.INVENTORY,
        date: new Date(),
        by: window.appUser?.fullName,
      };

      if (data.inventories?.items) {
        data.inventories.items.forEach((element) => {
          element.status = InventoryStatus.STORED;
          element.verificationRequired =
            element.verificationRequired === "true"
              ? "resolved"
              : element.verificationRequired;
          if (element.history) element.history = [...element.history, note];
          else element.history = [note];
        });
      }
    }

    return await transform(data);
  };

  const rejectTransform = async (data) => {
    debugger;
    setIsSaving(true);
    data.state = InboundState.REJECTED;
    data.verificationRequired = "resolved";

    return await transform(data);
  };

  const setNextState = (data) => {
    const effectiveAccount = getEffectiveAccount(data);
    if (data.releaseDateTime && data.releaseDateTime !== "") {
      data.status = InboundStatus.RELEASED;
    } else if (data.placedDateTime && data.placedDateTime !== "") {
      data.status = data.sit ? InboundStatus.SIT_PLACED : InboundStatus.PLACED;
      if (data.state === InboundState.NEW) {
        data.state = InboundState.PROCESS_SHIPMENT;
        data.lotNumber = data.lotNumberOverride
          ? data.lotNumber
          : autogenLotNumber(data.lotType, data, effectiveAccount);
      } else if (!data.startDateTime || data.startDateTime === "") {
        // startDateTime cleared (possible rollback)
        if (record.state !== InboundState.APPROVE_SHIPMENT) {
          // rollback and reset exceptions only if not pending approval
          data.state = InboundState.PROCESS_SHIPMENT;
          resetShipmentExceptions(data);
        }
      }
    } else if (data.notifyDateTime && data.notifyDateTime !== "") {
      data.status = InboundStatus.ARRIVED;
      data.state = InboundState.NEW;
      resetShipmentExceptions(data);

      data.lotNumber = data.lotNumberOverride
        ? data.lotNumber
        : autogenLotNumber(data.lotType, data, effectiveAccount);
    } else if (data.status?.startsWith(InboundStatus.ADVICE_ALL)) {
      // Any advice status (PROCESSING / REVIEW / FAILED / REJECTED) keeps its
      // status on a plain save; only reset state. Promotion to
      // IN_TRANSIT/SIT_PENDING (and clearing verificationRequired) happens
      // explicitly on approval via approveTransform /
      // handleDialogConfirmApproveAdvice. Prefix-matched so a rejected advice
      // — whose only toolbar button is Save — is not silently promoted into
      // the live lifecycle without a TrackCar or an approval email.
      data.state = InboundState.NEW;
    } else {
      data.status = data.sit
        ? InboundStatus.SIT_PENDING
        : InboundStatus.IN_TRANSIT;
      data.state = InboundState.NEW;
      data.verificationRequired = null;
    }

    return data;
  };

  const saveTransform = async (data) => {
    debugger;
    setIsSaving(true);
    setNextState(data);

    const inventories = data.inventories?.items || [];
    const isStockImportFullyLocated =
      data.stockImport &&
      inventories.length > 0 &&
      inventories.every((inv) => inv.inventoryItems?.items?.length > 0);
    if (isStockImportFullyLocated) {
      data.status = InboundStatus.RELEASED;
      data.state = InboundState.COMPLETED;
      if (!data.releaseDateTime) data.releaseDateTime = new Date();
      if (!data.endDateTime) data.endDateTime = new Date();
      inventories.forEach((inv) => {
        if (!inv.id) return;
        inv.status = InventoryStatus.STORED;
        if (inv.quantityReceived == null)
          inv.quantityReceived = inv.quantityAdvised;
        if (inv.quantityOnHand == null)
          inv.quantityOnHand = inv.quantityReceived ?? inv.quantityAdvised;
        inv.inventoryItems?.items?.forEach((item) => {
          item.status = InventoryStatus.STORED;
        });
      });
    }

    return await transform(data);
  };

  const transform = async (data) => {
    const effectiveAccount = getEffectiveAccount(data);
    // A previous FAILED save may have left a POD email pending; this save may
    // no longer be a release — start clean. Same for a pending check-in
    // cancel: it must be re-derived from this save's own rollback check.
    podEmailPendingRef.current = false;
    pendingGateCancelRef.current = false;
    pendingGateEditRef.current = false;
    pendingManualCheckInRef.current = false;
    pendingManualOpeningRef.current = false;
    data = { ...data };
    debugger;

    // Pending gate check-in edit staged by the dispatch tab's inline control;
    // persisted below by this same Save. Pulled out first — it is not a
    // shipment field.
    const gateEdit = data.gateCheckInEdit;
    delete data.gateCheckInEdit;

    // Declared here (not inside try) so they are in scope for the
    // __saveContext stamp below, after the try/catch.
    let isRollback = false;
    // The TrackCar to take off Track Management, or to put back. The lambda
    // pulls or restores it in the same transaction as this save, so the car
    // and the shipment cannot disagree about whether it is on a track
    // whichever write fails. Never both: a pull wins.
    let trackCarPullId = null;
    let trackCarRestoreId = null;

    try {
      data.billToAccountId =
        data.billToAccountId ||
        effectiveAccount?.defaultBillToAccountId ||
        data.accountId;
      data.railCarNumber = data.railCarNumber ? data.railCarNumber : "";
      data.poNumber = data.poNumber ? data.poNumber : "";
      data.normalizedPoNumber = normalizeValue(data.poNumber);
      data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);
      data.zoneId = data.zoneId || " ";

      data.scheduledDateTime = data.scheduledDateTime
        ? data.scheduledDateTime
        : null;
      if (data.status === InboundStatus.RELEASED) {
        if (data.sit) data.state = InboundState.COMPLETED;

        // Demurrage runs notified through released, both days counted.
        // Storage (placed through pulled) is computed where it is billed.
        data.daysOnDemurrageClock =
          getYardCalendarDaysInclusive(
            data.notifyDateTime,
            data.releaseDateTime,
          ) ?? 0;
        data.stripeBillingDateTime = new Date();
      } else data.daysOnDemurrageClock = 0;

      const hasWeights = data.inventories?.items?.every(
        (itm) => itm.weight != null && !isNaN(itm.weight),
      );
      if (hasWeights) {
        const weightUOM =
          data.weightUnits || data.inventories.items[0]?.weightUOM;
        data.netWeight = calcNetWeight(data, null, weightUOM);
        if (data.netWeight) data.weightUnits = weightUOM;
      }

      if (isBulkShipment(data.reloadYardCommodityId))
        data.workflowType = WorkflowType.BULK;
      else data.workflowType = WorkflowType.ACCEPT_STORE;

      data.customFields = getCustomFieldsToSave(
        data.customFields,
        effectiveAccount.inboundCustomFields,
      );

      const ignoreList = [
        "areaId",
        "zoneId",
        "reloadYardCommodityId",
        "createDateTime",
        "carrierEquipmentTypeId",
        "gateCheckInEdit",
        "collectedDateTime",
      ];
      const includeArrayList = ["customFields"];
      data.history = historyAutoGen(form, data, ignoreList, includeArrayList);
      const timePulledNote = buildTimePulledNote(record, data);
      if (timePulledNote)
        data.history = data.history
          ? [...data.history, timePulledNote]
          : [timePulledNote];

      if (data.carrierEquipmentTypeId !== record.carrierEquipmentTypeId) {
        const previousCarrierEquipmentTypeName =
          getCarrierEquipmentTypeNameById(record.carrierEquipmentTypeId);
        const nextCarrierEquipmentTypeName = getCarrierEquipmentTypeNameById(
          data.carrierEquipmentTypeId,
        );
        const note = {
          text: `Carrier Equipment Type Changed from ${previousCarrierEquipmentTypeName} to ${nextCarrierEquipmentTypeName}`,
          by: window.appUser?.fullName,
          date: new Date(),
          type: NoteType.SHIPMENT,
        };
        data.history = data.history ? [...data.history, note] : [note];
      }

      const newStatus = data.status;
      const oldStatus = record.status;
      if (newStatus !== oldStatus) {
        const note = {
          text: `Shipment advanced | status: ${newStatus} ${getLogState(
            data.state,
          )}`,
          by: window.appUser?.fullName,
          date: new Date(),
          type: NoteType.SHIPMENT,
        };
        data.history = data.history ? [...data.history, note] : [note];
      }

      isRollback = isStatusRollback(data);

      debugger;
      data.timeslot =
        selectedTimeslot === undefined ? record.timeslot : selectedTimeslot;
      // Remove car from track when rolling back before placement (consistent with outbound)
      if (isRollback && !data.placedDateTime && record.trackCar) {
        trackCarPullId = record.trackCar.id;
      }

      // Placing again after such a rollback has to undo it, or the deactivated
      // TrackCar stays invisible to every track view for the rest of the
      // shipment's life. No-ops when the car is already active.
      if (data.placedDateTime && !record.placedDateTime && record.trackCar) {
        trackCarRestoreId = record.trackCar.id;
      }

      // Clearing Time Collected puts the car back: isCollected off, and the
      // TrackCar the pull deactivated restored (to its last track, or to
      // Unspotted when the pull blanked it). Clearing the stamp alone left
      // both in place — billing still read the car as collected and every
      // track view still showed it gone. A rollback before placement in the
      // same save still takes the car off: an unplaced car is not on a track.
      if (isCollectedCleared(record, data)) {
        data.collectedDateTime = null;
        data.isCollected = false;
        if (record.trackCar && !trackCarPullId)
          trackCarRestoreId = record.trackCar.id;
      }

      // Rolling back past ARRIVED (notifyDateTime cleared) strands the
      // driver's gate check-in — the crew Gate Queue and the driver's phone
      // would keep a phantom arrival. Stash it for onSuccess to cancel AFTER
      // the shipment write commits; the shipment's lock clears on this same
      // save via gateCheckInId. The history note rides this save — should the
      // later cancel fail, onSuccess raises a warning rather than hiding it.
      if (
        record.notifyDateTime &&
        (!data.notifyDateTime || data.notifyDateTime === "") &&
        (data.status === InboundStatus.IN_TRANSIT ||
          data.status === InboundStatus.SIT_PENDING)
      ) {
        data.gateCheckInId = null;
        const strandedCheckIn = await fetchActiveGateCheckIn("in", record.id);
        if (strandedCheckIn) {
          pendingGateCancelRef.current = strandedCheckIn;
          data.history = [
            ...(data.history || []),
            {
              text: "Gate check-in cancelled — arrival rolled back",
              by: window.appUser?.fullName,
              date: new Date(),
              type: NoteType.SHIPMENT,
            },
          ];
        }
      }

      // Resolve the gate status/dock staged on the dispatch tab against the
      // row and status the manager was looking at when they staged it
      // (gateEdit.checkInId / gateEdit.baseStatus) — a row the gate crew
      // advanced or replaced meanwhile raises the conflict here and aborts
      // the whole save rather than being silently regressed. The write itself
      // waits for onSuccess, after the shipment commits. A rollback above is
      // about to cancel the row, so a staged edit (the CANCELLED pick that
      // caused the rollback) has nothing left to persist. A terminal staged
      // status will release the shipment's gateCheckInId lock when it
      // commits; mirror it in this save's own write so the shipment row does
      // not resurrect the lock.
      // A manual edit has no row behind it yet — it opens the one the mint
      // below creates, and resolving it would raise the conflict every time.
      if (gateEdit?.manual) pendingManualOpeningRef.current = gateEdit;
      else if (!pendingGateCancelRef.current && gateEdit) {
        try {
          const target = await resolveStagedGateEdit("in", record.id, gateEdit);
          if (target) {
            pendingGateEditRef.current = { target, gateEdit };
            if (GATE_TERMINAL_STATUSES.includes(gateEdit.status))
              data.gateCheckInId = null;
          }
        } catch (err) {
          if (err instanceof GateCheckInConflictError) {
            // Drop the stale staged edit so the manager's next save isn't
            // poisoned by it; the poll shows them the row's real status.
            form.change("gateCheckInEdit", undefined);
            notify(
              "This check-in changed at the gate — nothing was saved. Review the gate status and save again.",
              "warning",
            );
          }
          throw err;
        }
      }

      if (data.notes?.length > 0) {
        data.notes.forEach((element) => {
          if (!element.date) {
            element.type = NoteType.GENERIC;
            element.date = new Date();
            element.by = window.appUser?.fullName;
          }
        });
      } else data.notes = undefined;

      // Every field the manual gate check-in reads is settled by now. Merged
      // over the record so a save that left a field untouched still carries
      // it — the form only holds what its own tabs put there.
      pendingManualCheckInRef.current = { ...record, ...data };

      // Files upload BEFORE the lambda write (which now happens later, in the
      // dataProvider's inboundShipments branch — the single writer) so the
      // lambda persists the final attachment/receipt keys. The trailing
      // AppSync updateInboundShipment leg that used to save the parent is gone.
      await Promise.all([
        saveRemoveFiles(
          record,
          data,
          "attachments",
          `${FilePaths.INBOUND}/${Date.now()}`,
        ),
        saveRemoveFile(
          record,
          data,
          "receiptFile",
          `${FilePaths.INBOUND}/${Date.now()}`,
        ),
      ]);

      if (
        data.status !== oldStatus &&
        data.status === InboundStatus.RELEASED &&
        effectiveAccount?.podEmail &&
        !data.stockImport &&
        // for rail-track shipments POD sent from mobile on COMPLETE because not released until empty.
        !(isTrack(data.areaId) && data.carrierType === CarrierType.RAIL)
      ) {
        // Send the POD email only after persistence succeeded (onSuccess).
        // The history entry stays here — it must be part of the record the
        // lambda writes; if the email later fails it is logged non-fatally.
        // Stash the UNSANITIZED record: onSuccess only receives the
        // dataProvider's sanitized return, which lacks the connections the
        // POD body reads (inventories, photos, seals, account).
        podEmailPendingRef.current = { record: data, effectiveAccount };

        data.history = [
          ...data.history,
          {
            text: `POD sent to: ${effectiveAccount?.podEmail}`,
            by: window.appUser?.fullName,
            date: new Date(),
            type: NoteType.GENERIC,
          },
        ];
      }
    } catch (err) {
      // The gate conflict already notified with its own specific message.
      if (!(err instanceof GateCheckInConflictError)) {
        logError(
          "inboundShipments:DialogSaveButton:transform",
          err,
          notify,
          redirect,
        );
      }
      setIsSaving(false);
      throw err;
    }

    debugger;
    // NOTE: no sanitizeInbound here — the dataProvider's inboundShipments
    // branch sanitizes on return; lambdaSaveInbound's child-diffing needs the
    // unsanitized record (inventories.items, seals, manualCharges.items, etc.).

    // cleanup form side-effects
    delete data.commodity;
    delete data.customFieldSelect;
    delete data.inValidSealNumber;
    delete data.inValidSeals;
    delete data.validSeals;

    // AppSync auto-stamped updatedAt; the direct lambda write does not.
    data.updatedAt = new Date().toISOString();
    // Carry save context to the dataProvider's inboundShipments branch;
    // stripped there before the lambda call. Its presence is also the opt-in
    // gate — RA updates without it pass through to AppSync.
    data.__saveContext = { isRollback, trackCarPullId, trackCarRestoreId };

    return data;
  };

  const onSuccess = async ({ data }) => {
    debugger;
    // Every gate consequence of this save, in order, shared with the outbound
    // dialog. Awaited so its warnings land before the redirect below tears the
    // form down. The refs are read and cleared here, not passed in, so a
    // re-entrant save cannot see work this one has already taken.
    const strandedCheckIn = pendingGateCancelRef.current;
    pendingGateCancelRef.current = false;
    const stagedGateEdit = pendingGateEditRef.current;
    pendingGateEditRef.current = false;
    const savedShipment = pendingManualCheckInRef.current;
    pendingManualCheckInRef.current = false;
    const manualGateOpening = pendingManualOpeningRef.current;
    pendingManualOpeningRef.current = false;
    await runGateSideEffects({
      outbound: false,
      notify,
      strandedCheckIn,
      stagedGateEdit,
      savedShipment,
      manualGateOpening,
    });

    if (podEmailPendingRef.current) {
      // Use the unsanitized record stashed in transform — the `data` param
      // here is the dataProvider's sanitized return, stripped of the
      // connections the POD body renders.
      const { record: podRecord, effectiveAccount } =
        podEmailPendingRef.current;
      // Do not wait emailPOD.
      emailPOD(
        podRecord,
        effectiveAccount?.podEmail,
        null,
        effectiveAccount?.podPhotos,
        effectiveAccount?.podAttachments,
        notify,
      );
      podEmailPendingRef.current = false;
    }

    if (
      data.status === InboundStatus.RELEASED &&
      record.status !== InboundStatus.RELEASED
    ) {
      updateStripeUsage(data);
    }

    // Notify the advice submitter when their inbound advice is approved
    // (promoted from review to the live inbound lifecycle).
    const wasAdviceReview =
      record.status === InboundStatus.ADVICE_REVIEW ||
      record.status === InboundStatus.ADVICE_FAILED;
    const promoted =
      data.status === InboundStatus.IN_TRANSIT ||
      data.status === InboundStatus.SIT_PENDING;
    if (wasAdviceReview && promoted && data.advisedBy) {
      try {
        const emailContent = emailInboundAdviceApprovedHTML(
          window.appUser.firstName,
          window.appUser.email,
          window.appUser.reloadYard,
          data,
        );
        API.post("transREST", "/sendEmail", {
          body: {
            to: data.advisedBy,
            subject: "Inbound Advice Approved",
            template: "emailTemplate.html",
            htmlContent: emailContent,
          },
        });
      } catch (error) {
        logError(
          "inboundShipments:DialogSaveButton:adviceApprovedEmail",
          error,
          false,
        );
      }
    }

    notify("Inbound updated successfully", "success");
    // backPath comes from getInboundBackPath: the SIT list for a pending or
    // placed SIT railcar (even one released by this save), otherwise the
    // inbound list or the explicit backPath the page was opened with.
    deepRedirect(history, backPath ? backPath : basePath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    if (isUserFacingError(error)) {
      // The lambda names its refusals for the person saving — the crew moved
      // the car while the form was open — so those read verbatim. Still
      // recorded, without the generic toast on top.
      notify(error.message, "warning");
      logError("inboundShipments:DialogSaveButton:onFailure", error, false);
      return;
    }
    // AppSync errors carry .errors[]; lambda errors from the dataProvider
    // branch do not — guard both shapes.
    const message = error?.errors?.[0]?.message || error?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError(
        "inboundShipments:DialogSaveButton:data",
        formState.values,
        false,
      );
    }

    logError("inboundShipments:DialogSaveButton:onFailure", error, notify);
  };

  const { dirtyFields, pristine, submitting } = useFormState({
    subscription: { dirtyFields: true, pristine: true, submitting: true },
  });
  const isPristine =
    pristine ||
    !Object.keys(dirtyFields).some(
      (key) => key !== "photos" && key !== "photos.items",
    );

  // Spread the Toolbar-injected props (incl. handleSubmitWithRedirect) into
  // FormDataConsumer so the render-prop rest forwards them to child buttons
  // such as AdviceRejectButton.
  return (
    <FormDataConsumer {...props}>
      {({ formData, ...props }) => (
        <>
          <div>
            {formData.status === InboundStatus.ADVICE_PROCESSING ? (
              <SaveButton
                label="Save"
                className={classes.saveButton}
                submitOnEnter={false}
                undoable={false}
                disabled={true}
                saving={isSaving || submitting}
                handleSubmitWithRedirect={() => handleValidateSave(formData)}
                transform={saveTransform}
                onSuccess={onSuccess}
                onFailure={onFailure}
              />
            ) : (
              <SaveButton
                label="Save"
                className={classes.saveButton}
                submitOnEnter={false}
                undoable={false}
                disabled={isPristine || isSaving || isDeleting || loading}
                saving={isSaving || submitting}
                handleSubmitWithRedirect={() => handleValidateSave(formData)}
                transform={saveTransform}
                onSuccess={onSuccess}
                onFailure={onFailure}
              />
            )}

            {formData.status === InboundStatus.ADVICE_REVIEW ||
            formData.status === InboundStatus.ADVICE_FAILED ? (
              <>
                <SaveButton
                  label="Approve"
                  className={classes.saveButton}
                  submitOnEnter={false}
                  undoable={false}
                  disabled={isSaving || isDeleting || loading}
                  saving={isSaving || submitting}
                  handleSubmitWithRedirect={() =>
                    handleValidateApprove(formData)
                  }
                  transform={approveTransform}
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                  icon={<ApproveIcon />}
                />
                {/* ADVICE_REJECTED means "returned to the submitter for
                    correction" — the customer portal lists it with a Resubmit
                    form. A yard-created advice (no advisedBy) has no submitter
                    to return it to, nobody to notify, and no manager path back
                    out of that status, so Reject is hidden: the manager fixes
                    the advice in place with Save, or Approves it. */}
                {isExternalAdvice(record) && (
                  <AdviceRejectButton
                    {...props}
                    record={record}
                    backPath={backPath ? backPath : basePath}
                    isSaving={isSaving}
                    setIsSaving={setIsSaving}
                    isDeleting={isDeleting}
                    loading={loading}
                  />
                )}
              </>
            ) : (
              (formData.verificationRequired === "true" ||
                (formData.hasException &&
                  formData.state === InboundState.PROCESS_SHIPMENT)) && (
                <SaveButton
                  label={getApproveLabel()}
                  className={classes.saveButton}
                  submitOnEnter={false}
                  undoable={false}
                  disabled={
                    !formData.placedDateTime ||
                    isSaving ||
                    isDeleting ||
                    loading
                  }
                  saving={isSaving || submitting}
                  handleSubmitWithRedirect={() =>
                    handleValidateApprove(formData)
                  }
                  transform={approveTransform}
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                  icon={<ApproveIcon />}
                />
              )
            )}
            {((formData.verificationRequired === "true" &&
              formData.state === InboundState.APPROVE_SHIPMENT) ||
              (formData.hasException &&
                formData.state === InboundState.PROCESS_SHIPMENT)) && (
              <SaveButton
                label="Reject"
                variant="outlined"
                color="default"
                className={classnames("ra-save-button", classes.rejectButton)}
                submitOnEnter={false}
                undoable={false}
                disabled={isSaving || isDeleting || loading}
                saving={isSaving || submitting}
                handleSubmitWithRedirect={() => handleValidateReject(formData)}
                transform={rejectTransform}
                onSuccess={onSuccess}
                onFailure={onFailure}
                icon={<RejectIcon />}
              />
            )}
          </div>
          <ConditionalConfirm
            isOpen={open}
            loading={isSaving}
            title="Save Confirmation"
            content={confirmContent}
            onConfirm={onConfirm}
            onClose={handleDialogClose}
          />
        </>
      )}
    </FormDataConsumer>
  );
};
